import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Animo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { OpcionesDePregunta } from '../../components/onboarding/marca/OpcionesDePregunta';
import type { Reaccion } from '../../lib/onboardingMarca/dialogo';
import { leerEscaneo } from '../../lib/onboardingMarca/escaneo';
import { guardarEstado, leerEstado } from '../../lib/onboardingMarca/estado';
import { empresaYaGenero } from '../../lib/onboardingMarca/yaGenero';
import { enGuion } from '../../lib/onboardingMarca/guion';
import { detectarPlataforma, dominioDe } from '../../lib/onboardingMarca/link';
import { RESPUESTA_OTRO, type RespuestasDeLaEspera } from '../../lib/onboardingMarca/marca';
import {
  dialogoDePregunta,
  preguntaPendiente,
  preguntasDelOnboarding,
  preguntaSegunElLink,
  reaccionARespuesta,
  type EstadoDeLaLectura,
} from '../../lib/onboardingMarca/preguntas';

/** Cuánto se queda la respuesta elegida, con Cumbre reaccionando, antes de la siguiente. */
const MS_REACCION = 1700;
const POLL_MS = 2000;

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Cómo va la lectura de la web, mientras la persona responde. */
function useLectura(escaneoId: string | null): EstadoDeLaLectura {
  const [estado, setEstado] = useState<EstadoDeLaLectura>(escaneoId ? 'leyendo' : 'sin-web');
  useEffect(() => {
    if (!escaneoId) return;
    let vivo = true;
    let timer: number | undefined;
    const tick = async () => {
      const r = await leerEscaneo(escaneoId);
      if (!vivo) return;
      if (r?.estado === 'listo') return setEstado('leida');
      if (r?.estado === 'fallo') return setEstado('incompleta');
      timer = window.setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, [escaneoId]);
  return estado;
}

/**
 * A2 · Las preguntas (ADR-2026-10-02-onboarding-v2-preguntas-con-cumbre). Un paso propio
 * entre el link y el tablero: las hace Cumbre, de a una. Primero termina de preguntar y
 * después entran las opciones; al elegir, reacciona y pasa a la siguiente. Con link, la web
 * se sigue leyendo en el backend mientras tanto. Las respuestas quedan en el estado del
 * onboarding y el tablero las usa al confirmar la marca.
 */
export function PreguntasDeMarca() {
  const navigate = useNavigate();
  const inicial = useMemo(() => leerEstado(null), []);
  const companyId = inicial?.companyId ?? null;
  const manual = (inicial?.origen ?? (inicial?.escaneoId ? 'web' : 'manual')) === 'manual';
  const dominio = inicial?.link ? dominioDe(inicial.link) : '';
  const sugerida = useMemo(() => {
    const p = inicial?.link ? detectarPlataforma(inicial.link) : null;
    return p === 'tiendanube' || p === 'shopify' ? p : null;
  }, [inicial]);
  const lectura = useLectura(inicial?.escaneoId ?? null);
  const [quieto] = useState(pideMenosMovimiento);

  const [respuestas, setRespuestas] = useState<RespuestasDeLaEspera>(inicial?.respuestas ?? {});
  const [confirmadas, setConfirmadas] = useState<string[] | null>(null);
  const [reaccion, setReaccion] = useState<Reaccion | null>(null);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const [escribiendo, setEscribiendo] = useState(false);
  const [yaRespondio, setYaRespondio] = useState(false);
  const cuenta = useRef(0);
  const timer = useRef<number>();

  // Sin estado (entró directo a la URL): vuelve al link.
  useEffect(() => {
    if (!companyId) navigate(ONBOARDING_V2.marcaSetup, { replace: true });
  }, [companyId, navigate]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // ¿Ya generó (en la prueba gratis)? Entonces no se le pregunta qué crear primero: esa respuesta
  // arma las primeras piezas, que tampoco se le muestran
  // (ADR-2026-10-31-las-primeras-piezas-solo-si-no-genero). Se pregunta una vez y queda en el
  // estado, que leen el tablero y las piezas. Null mientras se pregunta.
  const [yaGenero, setYaGenero] = useState<boolean | null>(
    () => inicial?.yaGenero ?? (enGuion() || !companyId ? false : null),
  );
  useEffect(() => {
    if (yaGenero !== null || !companyId) return;
    let vivo = true;
    void empresaYaGenero(companyId).then((si) => {
      if (!vivo) return;
      guardarEstado({ companyId, yaGenero: si });
      setYaGenero(si);
    });
    return () => {
      vivo = false;
    };
  }, [yaGenero, companyId]);
  const preguntas = useMemo(() => preguntasDelOnboarding(yaGenero === true), [yaGenero]);

  const indice = preguntaPendiente(respuestas, preguntas);
  const pregunta = useMemo(
    () => (indice >= 0 ? preguntaSegunElLink(preguntas[indice], !manual) : null),
    [indice, manual, preguntas],
  );

  // Con todo respondido no hay nada que decir: este paso no tiene cierre propio y va directo
  // al tablero, termine o no el análisis (ahí se ve lo que haya).
  useEffect(() => {
    if (companyId && yaGenero !== null && indice < 0) navigate(ONBOARDING_V2.setupMarca, { replace: true });
  }, [companyId, yaGenero, indice, navigate]);

  const tramos = useMemo(
    () => (yaGenero !== null && indice >= 0 ? dialogoDePregunta({ indice, manual, sugerida, preguntas }) : []),
    [yaGenero, indice, manual, sugerida, preguntas],
  );
  // La primera vez espera a que Cumbre llegue; entre preguntas, casi nada.
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: yaRespondio ? 250 : 1000 });

  /**
   * Confirma lo que marcó (ADR-2026-10-02-onboarding-v2-preguntas-de-varias): la principal
   * —la primera— en su campo de siempre, y todo en `todas`. "Otro" guarda su texto.
   */
  const responder = (ids: string[], textoOtro: string) => {
    if (!pregunta || confirmadas || !companyId || !ids.length) return;
    const { clave } = pregunta;
    const otros = { ...respuestas.otros };
    if (ids.includes(RESPUESTA_OTRO) && textoOtro) otros[clave] = textoOtro;
    else delete otros[clave];
    // `otros` y `todas` van enteros: las respuestas se mezclan con un spread de un nivel.
    const parcial = { [clave]: ids[0], todas: { ...respuestas.todas, [clave]: ids }, otros } as RespuestasDeLaEspera;
    cuenta.current += 1;
    setConfirmadas(ids);
    setEscribiendo(false);
    setReaccion(reaccionARespuesta(clave, ids, cuenta.current));
    setGesto({ tipo: 'festeja', n: cuenta.current });
    timer.current = window.setTimeout(() => {
      const nuevas = { ...respuestas, ...parcial };
      guardarEstado({ companyId, respuestas: nuevas });
      // La última: después de la reacción de Cumbre, directo al tablero.
      if (preguntaPendiente(nuevas, preguntas) < 0) {
        navigate(ONBOARDING_V2.setupMarca);
        return;
      }
      setRespuestas(nuevas);
      setConfirmadas(null);
      setReaccion(null);
      setYaRespondio(true);
    }, quieto ? 600 : MS_REACCION);
  };

  // El progreso de las preguntas entra cuando Cumbre termina la primera; después se queda
  // (entre pregunta y pregunta no desaparece, así se ve llenarse).
  const [conProgreso, setConProgreso] = useState(quieto);
  if (yaGenero !== null && dialogo.terminado && !conProgreso) setConProgreso(true);

  const animo: Animo = escribiendo ? 'mira' : 'escucha';
  // La que se acaba de elegir ya cuenta, aunque se guarde cuando Cumbre termina de reaccionar.
  const respondidas = preguntas.filter((q) => respuestas[q.clave]).length + (confirmadas ? 1 : 0);

  /** Cómo va la lectura de la web. Se dibuja en dos lugares y el CSS muestra uno: en el
   *  teléfono, debajo del saludo de Cumbre; en desktop, debajo del progreso. Fija abajo de la
   *  pantalla pisaba el botón "Listo". */
  const lecturaEnPantalla = (lugar: string) =>
    !manual && dominio ? (
      <p className={`om-lectura ${lugar} ${lectura === 'leyendo' ? '' : 'is-lista'}`} role="status">
        {lectura === 'leyendo' ? (
          <span className="om-lectura__punto om-pulso" aria-hidden="true" />
        ) : (
          <span className="om-lectura__tilde material-symbols-rounded" aria-hidden="true">
            check
          </span>
        )}
        <span>{lectura === 'leyendo' ? `Analizando ${dominio}…` : `Análisis de ${dominio} listo`}</span>
      </p>
    ) : null;

  // Mientras se pregunta si ya generó, sólo el fondo: la cantidad de preguntas depende de eso.
  if (yaGenero === null) {
    return (
      <div className="relative isolate min-h-[calc(100dvh-var(--om-barra))] overflow-hidden">
        <FondoCordillera quieto={quieto} />
      </div>
    );
  }

  return (
    <div className="relative isolate min-h-[calc(100dvh-var(--om-barra))] overflow-hidden">
      <FondoCordillera quieto={quieto} />

      <div className="mx-auto flex max-w-[640px] flex-col gap-5 px-4 pb-48 pt-6 md:gap-6 md:pt-8">
        <GuiaCumbre
          tramos={tramos}
          letras={dialogo.letras}
          hablando={dialogo.hablando}
          terminado={dialogo.terminado}
          onCompletar={dialogo.completar}
          reaccion={reaccion}
          reaccionEnLugarDeLaPregunta
          junto={lecturaEnPantalla('om-lectura--telefono')}
          animo={animo}
          gesto={gesto}
          instantaneo={quieto}
        />

        {/* Cuántas van: un círculo por pregunta, con su tema. A propósito distinto de la
            barra de pasos del onboarding, que está arriba. */}
        {conProgreso && (
          <ol className={`om-progreso ${quieto ? '' : 'is-entrando'}`} aria-label={`${respondidas} de ${preguntas.length} preguntas respondidas`}>
            {preguntas.map((q, i) => {
              const estado = i < respondidas ? 'is-hecha' : i === indice ? 'is-actual' : '';
              return (
                <li key={q.clave} className={`om-progreso__paso ${estado}`}>
                  {i < respondidas ? (
                    // El tilde en SVG y no como glifo: Material Symbols ya está en su peso
                    // máximo (700) y el trazo se veía finito.
                    <span className="om-progreso__nodo" aria-hidden="true">
                      <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]">
                        <path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  ) : (
                    <span className="om-progreso__nodo material-symbols-rounded" aria-hidden="true">
                      {q.icono}
                    </span>
                  )}
                  <span className="sr-only">
                    {q.titulo} {i < respondidas ? '(respondida)' : i === indice ? '(ahora)' : ''}
                  </span>
                </li>
              );
            })}
          </ol>
        )}

        {/* Cómo va la lectura de la web, en desktop: debajo del progreso (siempre en el mismo
            lugar entre pregunta y pregunta). En el teléfono va debajo del saludo de Cumbre. */}
        {lecturaEnPantalla('om-lectura--escritorio')}

        {/* Las opciones esperan a que Cumbre termine de preguntar. */}
        {pregunta && dialogo.terminado && (
          <OpcionesDePregunta
            key={pregunta.clave}
            pregunta={pregunta}
            confirmadas={confirmadas}
            sugerida={pregunta.clave === 'canal' ? sugerida : null}
            onConfirmar={responder}
            onEscribiendo={setEscribiendo}
          />
        )}

      </div>
    </div>
  );
}

export default PreguntasDeMarca;
